import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { selectUser, selectRealUser } from "../../features/userSlice";
import { update } from "../../features/roleSlice";
import { store } from "../../features/eventSlice";

import { startLiveSession } from "../../utils/liveSession";
import { callMsCalendar } from "../../utils/MsGraphApiCall";
import { loginRequest } from "../../authConfig";
import { setPostLoginRedirect } from "../../utils/postLoginRedirect";

import { SideNavigation } from "./SideNavigation";
import ThemeMenu from "./ThemeMenu";
import CommandPalette, { isMac } from "./CommandPalette";
import NotificationButton from "../Other/NotificationButton";
import MessagesButton from "../Other/MessagesButton";
import ImpersonationBanner from "./ImpersonationBanner";
import FeatureRequestBubble from "../Other/FeatureRequestBubble";
import FeedbackCheckIn from "../Other/FeedbackCheckIn";
import { useTheme } from "../../theme/ThemeContext";

import MenuIcon from "@mui/icons-material/MenuRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";

// NavListAuth remounts on every route change; this gates the calendar
// fetch to once per full page load instead of once per click.
let calendarFetchedThisLoad = false;

const NavListAuth = () => {
  const user = useSelector(selectUser);
  // Session init must key off the actual signed-in account: during an admin
  // preview selectUser returns the previewed user, which would otherwise mask
  // an expired session and skip the /inituser rehydration.
  const realUser = useSelector(selectRealUser);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const { isDark, isGlass } = useTheme();

  const { instance } = useMsal();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Handle auth & roles
  useEffect(() => {
    if (!realUser) {
      // Remember the page the user actually asked for so InitUser can send
      // them back after authentication instead of the homepage.
      setPostLoginRedirect(
        window.location.pathname + window.location.search + window.location.hash
      );
      navigate("/inituser");
      return;
    }

    // Roles already arrived with the user from InitUser — seed the role
    // slice immediately so pages don't flash without permissions. From then
    // on the live session poll keeps roles, branch, and page grants in step
    // with the server for as long as the tab is open (utils/liveSession.js),
    // so admin changes land without a re-login.
    dispatch(update({ roles: user.roles || [] }));
    startLiveSession();

    if (!calendarFetchedThisLoad) {
      calendarFetchedThisLoad = true;
      callMsCalendar()
        .then((response) => dispatch(store(response)))
        .catch((e) => {
          if (e instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect({
              ...loginRequest,
              account: instance.getActiveAccount(),
            });
          }
        });
    }
  }, [user, realUser, dispatch, instance, navigate]);

  // ⌘K / Ctrl+K opens the quick-jump palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowPalette(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {/* Liquid Glass floats the bar inset from the edges; Classic keeps a
          full-width bar. The blurred material lives on its own layer
          (the aria-hidden span) rather than on the bar itself: an element
          with backdrop-filter becomes the "backdrop root" for its
          descendants, so the dropdowns inside (notifications, appearance)
          could then only blur the bar, not the page under them. */}
      <header
        className={`sticky top-0 z-30 w-full print:hidden ${
          isGlass ? "pointer-events-none px-2 pt-2 sm:px-3 sm:pt-3" : ""
        }`}
      >
        <div
          className={`pointer-events-auto relative ${
            isGlass ? "rounded-[1.25rem]" : ""
          }`}
        >
          <span
            aria-hidden="true"
            className={`app-chrome absolute inset-0 backdrop-blur-xl backdrop-saturate-150 ${
              isGlass
                ? "app-glass-rim rounded-[1.25rem] border shadow-[0_10px_30px_-12px_rgba(30,41,80,0.28)] dark:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.7)]"
                : "border-b"
            }`}
          />
          <div
            className={`relative flex items-center justify-between gap-2 text-[color:var(--app-text)] ${
              isGlass ? "h-14 px-2 sm:px-3" : "h-16 px-3 sm:px-6"
            }`}
          >
            {/* Left: menu + logo */}
            <div className="flex min-w-0 items-center gap-1 sm:gap-2">
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                aria-label="Open navigation menu"
                aria-expanded={showSidebar}
                className="app-icon-btn"
              >
                <MenuIcon fontSize="small" />
              </button>

              <Link
                to="/"
                className="flex items-center rounded-lg px-1"
                aria-label="Home"
              >
                <img
                  src={
                    isDark
                      ? require("../../assets/NodeLogo2.png")
                      : require("../../assets/NodeLogoDark.png")
                  }
                  alt="Big C Node"
                  className="h-7 w-auto sm:h-8"
                />
              </Link>
            </div>

            {/* Center: quick-jump search (opens the palette) */}
            <button
              type="button"
              onClick={() => setShowPalette(true)}
              className="app-muted hidden h-9 w-full max-w-sm flex-1 items-center gap-2 rounded-full border border-[color:var(--app-input-border)] bg-[color:var(--app-input-bg)] px-3.5 text-sm transition hover:border-sky-400/60 md:flex"
            >
              <SearchIcon sx={{ fontSize: 18 }} />
              <span className="flex-1 text-left">Search pages…</span>
              <kbd className="app-chip font-sans text-[11px]">
                {isMac() ? "⌘" : "Ctrl"} K
              </kbd>
            </button>

            {/* Right: actions + profile */}
            <div className="flex items-center gap-0.5 sm:gap-1.5">
              <button
                type="button"
                onClick={() => setShowPalette(true)}
                aria-label="Search pages"
                className="app-icon-btn md:hidden"
              >
                <SearchIcon fontSize="small" />
              </button>

              <MessagesButton />

              <NotificationButton />

              <ThemeMenu />

              <span className="mx-1 hidden h-6 w-px bg-[color:var(--app-divider)] sm:block" />

              <Link
                to="/profile"
                aria-label="View profile"
                className="group inline-flex items-center rounded-full"
              >
                <div className="h-9 w-9 overflow-hidden rounded-full bg-slate-300 ring-2 ring-white/70 shadow-sm transition group-hover:ring-sky-500 dark:bg-slate-700 dark:ring-white/10">
                  <img
                    src={`/api/profile/image?id=${user?.id}`}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar drawer */}
      <SideNavigation
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      />

      <CommandPalette open={showPalette} onClose={() => setShowPalette(false)} />

      <ImpersonationBanner />

      <FeatureRequestBubble />

      <FeedbackCheckIn />
    </>
  );
};

export default NavListAuth;
